import { MAX_ATTEMPTS, WORD_LENGTH } from './enums.js';
import { normalize, isWordShape } from './normalize.js';
import { evaluate } from './evaluate.js';
import { WordleError } from './errors.js';
export const DEFAULT_CONFIG = {
    wordLength: WORD_LENGTH,
    maxAttempts: MAX_ATTEMPTS,
};
/** Start a fresh game on a target word (spec §5 `initGame`). */
export function initGame(target, config = DEFAULT_CONFIG) {
    const t = normalize(target);
    if (!isWordShape(t, config.wordLength)) {
        throw new WordleError('INVALID_WORD', `target must be ${config.wordLength} letters`);
    }
    return { target: t, guesses: [], evaluations: [], status: 'in_progress', config };
}
/** The status a game reaches after a guess (won on a match, lost on the last miss). */
function statusAfter(guess, target, count, config) {
    if (guess === target)
        return 'won';
    if (count >= config.maxAttempts)
        return 'lost';
    return 'in_progress';
}
/**
 * Submit a guess (spec §5 `submitGuess`).
 *
 * Throws `GAME_OVER` if the game has already ended, and `INVALID_WORD` if the
 * guess is the wrong length or not in the lexicon — so a caller must handle a
 * rejected guess, never silently record it.
 */
export function submitGuess(state, guess, lexicon) {
    if (state.status !== 'in_progress') {
        throw new WordleError('GAME_OVER', 'the game is over');
    }
    const g = normalize(guess);
    if (!isWordShape(g, state.config.wordLength) || !lexicon.isValidGuess(g)) {
        throw new WordleError('INVALID_WORD', 'not a valid word');
    }
    const evaluation = evaluate(g, state.target);
    const guesses = [...state.guesses, g];
    const evaluations = [...state.evaluations, evaluation];
    const status = statusAfter(g, state.target, guesses.length, state.config);
    return { state: { ...state, guesses, evaluations, status }, evaluation };
}
/**
 * Rebuild a game's full state from its stored answer and guess list — the
 * persisted row (`target_word` + `guesses[]`) back into a playable/renderable
 * state. Evaluations are recomputed rather than stored, so the grid can never
 * drift from the guesses that produced it.
 */
export function restoreGame(target, guesses, config = DEFAULT_CONFIG) {
    let state = initGame(target, config);
    for (const guess of guesses) {
        const g = normalize(guess);
        const evaluation = evaluate(g, state.target);
        const nextGuesses = [...state.guesses, g];
        state = {
            ...state,
            guesses: nextGuesses,
            evaluations: [...state.evaluations, evaluation],
            status: statusAfter(g, state.target, nextGuesses.length, config),
        };
    }
    return state;
}
/**
 * The best state reached for each letter across all guesses — what colours the
 * on-screen keyboard. `exact` beats `present` beats `absent`, so a letter once
 * green never dims to yellow on a later guess.
 */
export function keyboardStates(state) {
    const rank = { absent: 0, present: 1, exact: 2 };
    const out = {};
    state.guesses.forEach((guess, gi) => {
        const ev = state.evaluations[gi];
        for (let i = 0; i < guess.length; i += 1) {
            const ch = guess[i];
            const s = ev[i];
            if (!(ch in out) || rank[s] > rank[out[ch]])
                out[ch] = s;
        }
    });
    return out;
}
//# sourceMappingURL=engine.js.map